import React from 'react';
import Styles from './statuses.module.scss';
import { useDealEstimations } from '~/hooks/deals/useDealEstimations';
import { useDealInspectionReportImages } from '~/hooks/deals/useDealInspectionReportImages';
import { ReportImageNavigation } from '~/components/common/reports/navigation/ReportImageNavigation';
import { PATH as INSPECTION_REPORT_PATH } from '~/pages/deals/[id]/inspection_reports';
import { UploadFileNavigation } from '~/components/common/upload_files/UploadFileNavigation';
import { StatusHandleClientButtons } from '~/components/common/statuses/StatusHandleClientButtons';
import { IN_CONSTRUCTION } from '~/models/Status';
import { DealUploadFile } from '~/models/DealUploadFile';
import { getFilePath as getClientFilePath } from '~/utils/api/deals/DealUploadedFile';
import { useDeals } from '~/hooks/useDeals';
import { matchDealType } from '~/models/Deal';
import { getOriginVendor } from '~/models/DealVendorCompany';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { useDealEstimationHeaders } from '~/hooks/deals/useDealEstimationHeaders';
import { estimationRequestSide } from '~/models/DealEstimationHeader';
import { ConstructionClientNavigation } from '~/components/common/constructions/navigation/ConstructionClientNavigation';

export const SendEstimate: React.FC = () => {
  const dealId = useIdQueryHandler();
  const { data: deal } = useDeals().show(dealId);
  const { data: dealEstimationContext } = useDealEstimations().index(dealId);
  const { data: dealEstimationHeaders } =
    useDealEstimationHeaders().index(dealId);
  const requestHeader =
    dealEstimationHeaders?.find((header) => header.status === 'request') ??
    null;
  const reportsApi = useDealInspectionReportImages();
  const { data: reportImages } = reportsApi.index(Number(dealId));
  const allowPartiallyFinish = React.useMemo(() => {
    if (deal === null) return false;
    const originVendor = getOriginVendor(deal);
    return originVendor !== null && originVendor.allowPartiallyFinished;
  }, [deal]);
  const forwardStatusDisabled = React.useMemo(() => {
    if (dealEstimationContext === null) return true;
    if (
      dealEstimationContext.dealConstructions.length === 0 ||
      requestHeader !== null
    )
      return true;
    return false;
  }, [dealEstimationContext, requestHeader]);
  if (deal === null || dealId === null) {
    return null;
  }
  const handleGetMemorandumFilePath = (file: DealUploadFile): string =>
    getClientFilePath(deal.id, file);
  return (
    <div>
      <h3 className={Styles.Title}>
        {`${
          matchDealType(deal, 'restoration') ? '退去' : ''
        }立会が完了しました`}
      </h3>
      {reportImages !== null && (
        <ReportImageNavigation
          detailPageButtonLabel="画像確認画面"
          detailPagePath={INSPECTION_REPORT_PATH(dealId)}
          reportImages={reportImages}
          title={`${
            matchDealType(deal, 'repair') ? '現地調査の' : '立会報告'
          }画像を確認してください`}
        />
      )}
      <ConstructionClientNavigation
        title={
          estimationRequestSide(deal, 'client')
            ? '見積りを入力してください'
            : '見積りを確認してください'
        }
      />
      {deal.dealMemorandumFiles.length > 0 && (
        <UploadFileNavigation
          deal={deal}
          files={deal.dealMemorandumFiles}
          fileType="賃借人同意書"
          handleGetFilePath={handleGetMemorandumFilePath}
          title="賃借人同意書を確認してください"
        />
      )}
      <StatusHandleClientButtons
        alertText="確定ボタンを押すと工事の手配へ移ります。"
        allowPartiallyFinish={allowPartiallyFinish}
        forwardDisabled={forwardStatusDisabled}
        nextStatusId={IN_CONSTRUCTION}
      />
    </div>
  );
};
